import {
  parseProject,
  fieldSchema,
  nodeSchema,
  pageSchema,
  outputSchema,
  type Project,
} from "./schema";
import { newStatementDataset } from "./statements";
const f = (
  id: string,
  label: string,
  type = "text",
  options: Record<string, unknown> = {},
) => fieldSchema.parse({ id, key: id, label, type, ...options });
const n = (
  id: string,
  type: string,
  x: number,
  y: number,
  width: number,
  height: number,
  options: Record<string, unknown> = {},
) => nodeSchema.parse({ id, name: id, type, x, y, width, height, ...options });
const outputs = [
  outputSchema.parse({
    id: "out_print",
    name: "Print · 300 DPI",
    format: "pdf",
  }),
  outputSchema.parse({
    id: "out_web",
    name: "Web · transparent PNG",
    format: "png",
    dpi: 150,
  }),
  outputSchema.parse({
    id: "out_cmyk",
    name: "Press · CMYK PDF",
    format: "pdf",
    colorSpace: "cmyk",
  }),
  outputSchema.parse({ id: "out_svg", name: "Vector · SVG", format: "svg" }),
];
export const botanical: Project = parseProject({
  schemaVersion: 1,
  id: "botanical",
  tenantId: "demo",
  name: "Botanical launch invitation",
  description: "An invitation that brings your next chapter to life.",
  instructions:
    "Personalize the invitation, then review your live proof. Switch to the workshop part to see inherited settings and overrides.",
  groups: [
    { id: "event", name: "Your event", column: 1, order: 0 },
    { id: "details", name: "The details", column: 2, order: 1 },
  ],
  tabs: [
    { id: "tab_event", name: "Event", order: 0, groupIds: ["event"] },
    { id: "tab_details", name: "Details", order: 1, groupIds: ["details"] },
  ],
  fields: [
    f("eventName", "Event name", "text", {
      groupId: "event",
      required: true,
      default: "A new season begins",
      proofName: true,
      validation: { maxLength: 60 },
    }),
    f("eventType", "Event type", "dropdown", {
      groupId: "event",
      default: "launch",
      choices: [
        { value: "launch", label: "Launch celebration" },
        { value: "workshop", label: "Creative workshop" },
        { value: "private", label: "Private gathering" },
      ],
      order: 1,
    }),
    f("subtitle", "Supporting line", "textarea", {
      groupId: "event",
      default:
        "A little gathering. A fresh perspective.\nAn invitation to grow together.",
      order: 2,
      validation: { maxLength: 180 },
    }),
    f("host", "Hosted by", "text", {
      groupId: "details",
      default: "Fern & Form Studio",
      order: 3,
      source: { kind: "user", key: "company" },
    }),
    f("date", "Date", "date", {
      groupId: "details",
      default: "2026-11-12",
      required: true,
      order: 4,
    }),
    f("time", "Time", "time", {
      groupId: "details",
      default: "18:00",
      order: 5,
    }),
    f("venue", "Location", "text", {
      groupId: "details",
      default: "The Glasshouse · Austin, TX",
      order: 6,
    }),
    f("website", "RSVP website", "text", {
      groupId: "details",
      default: "https://example.com/rsvp",
      order: 7,
    }),
    f("rsvp", "Include RSVP code", "checkbox", {
      groupId: "details",
      default: true,
      order: 8,
    }),
    f("qrField", "RSVP code", "qr", {
      hidden: false,
      editable: false,
      default: "https://example.com/rsvp",
      order: 9,
      qr: { kind: "website", mapping: { url: "website" } },
      visibleWhen: { field: "rsvp", op: "eq", value: true },
    }),
    f("seats", "Available seats", "number", {
      groupId: "details",
      default: 12,
      order: 10,
      validation: { min: 1, max: 100 },
      visibleWhen: { field: "eventType", op: "eq", value: "workshop" },
    }),
    f("logo", "Studio mark", "image", { groupId: "event", order: 11 }),
  ],
  parts: [
    {
      id: "workshop",
      name: "Workshop invitation",
      sku: "INV-WORKSHOP",
      overrides: {
        eventName: {
          default: "Make something meaningful",
          label: "Workshop title",
        },
        eventType: { default: "workshop" },
        venue: { default: "The Makers Table · Austin, TX" },
      },
      systemValues: {},
      instructions: "",
    },
    {
      id: "private_part",
      name: "Private gathering",
      sku: "INV-PRIVATE",
      overrides: { eventType: { default: "private" } },
      systemValues: {},
    },
  ],
  styles: [
    {
      id: "title",
      name: "Title Text",
      fontFamily: "Georgia",
      fontSize: 39,
      fontWeight: 400,
      color: "#193f38",
      lineHeight: 1.12,
      align: "center",
    },
    {
      id: "body",
      name: "Body Text",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 400,
      color: "#5b7367",
      lineHeight: 1.5,
      align: "center",
    },
  ],
  pages: [
    pageSchema.parse({
      id: "invitation",
      name: "Invitation",
      width: 360,
      height: 504,
      background: "#f8f5e9",
      margin: 24,
      nodes: [
        n("outer-border", "rect", 18, 18, 324, 468, {
          fill: "transparent",
          stroke: "#b9c6ac",
          strokeWidth: 0.8,
          radius: 1,
        }),
        n("studio-label", "text", 36, 48, 288, 18, {
          content: "F E R N   &   F O R M",
          fontSize: 10,
          align: "center",
          color: "#507360",
        }),
        n("petal-left", "ellipse", 84, 94, 55, 80, {
          fill: "#a7b9a1",
          rotation: -35,
          opacity: 0.6,
        }),
        n("petal-right", "ellipse", 137, 95, 55, 80, {
          fill: "#728f75",
          rotation: 30,
          opacity: 0.45,
        }),
        n("petal-top", "ellipse", 170, 86, 48, 72, {
          fill: "#d4dbbc",
          rotation: 55,
          opacity: 0.9,
        }),
        n("stem", "line", 173, 111, 1, 69, {
          stroke: "#668264",
          strokeWidth: 1.1,
        }),
        n("invitation-title", "text", 40, 188, 280, 100, {
          fieldId: "eventName",
          styleId: "title",
          overflow: "shrink",
        }),
        n("invitation-subtitle", "text", 44, 298, 272, 45, {
          fieldId: "subtitle",
          styleId: "body",
        }),
        n("divider", "line", 153, 353, 54, 0.2, {
          stroke: "#aab89c",
          strokeWidth: 0.8,
        }),
        n("date-line", "text", 45, 371, 270, 19, {
          fieldId: "date",
          fontSize: 12,
          align: "center",
          color: "#264c40",
        }),
        n("time-line", "text", 45, 393, 270, 18, {
          fieldId: "time",
          fontSize: 11,
          align: "center",
          color: "#5b7367",
        }),
        n("venue-line", "text", 30, 418, 300, 22, {
          fieldId: "venue",
          fontSize: 10,
          align: "center",
          color: "#5b7367",
        }),
        n("qr-layer", "qr", 163, 448, 34, 34, { fieldId: "qrField" }),
        n("logo-layer", "image", 281, 42, 32, 32, { fieldId: "logo" }),
      ],
    }),
  ],
  outputs,
});
export const businessCard: Project = parseProject({
  schemaVersion: 1,
  id: "business-card",
  name: "Two-sided business card",
  description: "A clean, two-sided card with shared contact information.",
  fields: [
    f("name", "Full name", "text", {
      default: "Alex Morgan",
      required: true,
      proofName: true,
    }),
    f("titleField", "Job title", "text", {
      default: "Creative director",
      order: 1,
    }),
    f("email", "Email", "email", { default: "alex@example.com", order: 2 }),
    f("phone", "Phone", "phone", { default: "+1 (512) 555-0142", order: 3 }),
    f("company", "Company", "text", { default: "FIELDWORK", order: 4 }),
    f("websiteCard", "Website", "text", { default: "example.com", order: 5 }),
  ],
  pages: [
    pageSchema.parse({
      id: "card_front",
      name: "Front",
      width: 252,
      height: 144,
      margin: 12,
      background: "#173d3b",
      nodes: [
        n("card_brand", "text", 20, 22, 212, 25, {
          fieldId: "company",
          fontSize: 21,
          fontWeight: 700,
          color: "#d7e8c5",
        }),
        n("card_name", "text", 20, 68, 212, 23, {
          fieldId: "name",
          fontSize: 17,
          color: "#ffffff",
        }),
        n("card_title", "text", 20, 95, 212, 18, {
          fieldId: "titleField",
          fontSize: 10,
          color: "#c4d5cb",
        }),
      ],
    }),
    pageSchema.parse({
      id: "card_back",
      name: "Back",
      width: 252,
      height: 144,
      margin: 12,
      background: "#f6f7f1",
      nodes: [
        n("card_email", "text", 20, 28, 212, 18, {
          fieldId: "email",
          fontSize: 11,
        }),
        n("card_phone", "text", 20, 52, 212, 18, {
          fieldId: "phone",
          fontSize: 11,
        }),
        n("card_web", "text", 20, 84, 212, 18, {
          fieldId: "websiteCard",
          fontSize: 11,
        }),
        n("card_rule", "line", 20, 118, 212, 0.2, { stroke: "#173d3b" }),
      ],
    }),
  ],
  outputs: [
    ...outputs.map((o) => ({ ...o, id: `card_${o.id}` })),
    outputSchema.parse({
      id: "business-card-16-up",
      name: "Business card 16-up",
      format: "pdf",
      colorSpace: "rgb",
      imposition: {
        enabled: true,
        sheetWidth: 1224,
        sheetHeight: 792,
        columns: 4,
        rows: 4,
        margin: 24,
        gap: 0,
        copies: 16,
        itemBleed: 9,
        cropMarks: { enabled: true, length: 9, offset: 3, strokeWidth: 0.5 },
      },
    }),
  ],
});
export const freeform: Project = parseProject({
  schemaVersion: 1,
  id: "freeform",
  name: "Transparent signature",
  description:
    "No background. No fixed size. Output grows to fit your content.",
  fields: [
    f("signatureName", "Name", "text", {
      default: "Alex Morgan",
      proofName: true,
    }),
    f("signatureRole", "Role", "text", {
      default: "Designer · Maker · Dreamer",
      order: 1,
    }),
  ],
  pages: [
    pageSchema.parse({
      id: "signature_page",
      name: "Signature",
      width: null,
      height: null,
      background: null,
      margin: 0,
      nodes: [
        n("signature_name", "text", 0, 0, 280, 36, {
          fieldId: "signatureName",
          fontFamily: "Georgia",
          fontSize: 28,
        }),
        n("signature_role", "text", 0, 45, 280, 20, {
          fieldId: "signatureRole",
          fontSize: 11,
          color: "#6f8473",
        }),
      ],
    }),
  ],
  outputs: outputs.map((o) => ({ ...o, id: `signature_${o.id}` })),
});
export const newsletter: Project = parseProject({
  schemaVersion: 1,
  id: "newsletter",
  name: "Flowing newsletter",
  description:
    "A two-column story that grows into continuation pages. Rich text, linked regions and repeating page bands share one layout engine.",
  documentSettings: {
    background: "#fffdf7",
    typography: { fontSize: 11, lineHeight: 1.4 },
  },
  styles: [
    {
      id: "newsletter_title_style",
      name: "Title Text",
      role: "title",
      inheritTypography: true,
      overrides: {
        fontFamily: "Georgia",
        fontSize: 21,
        fontWeight: 400,
        lineHeight: 1.35,
      },
    },
    {
      id: "newsletter_body_style",
      name: "Body Text",
      role: "body",
      inheritTypography: true,
      overrides: { fontSize: 11, fontWeight: 400, lineHeight: 1.4 },
    },
  ],
  fields: [
    f("newsletterTitle", "Title", "text", {
      default: "Notes from the studio",
      proofName: true,
    }),
    f("newsletterStory", "Story", "richtext", {
      order: 1,
      default:
        "<p><strong>A place for your next idea.</strong> This document grows with the story you tell.</p>" +
        "<p>Configure linked columns, then personalize this story. Text reflows through each region and onto the next page while its formatting stays intact.</p>".repeat(
          18,
        ) +
        '<p><span style="color:#26776a">Keep creating.</span> Every proof uses the same geometry as the exported document.</p>',
    }),
  ],
  pages: [
    pageSchema.parse({
      id: "newsletter_page",
      name: "Story",
      width: 420,
      height: 594,
      margin: 24,
      background: "#fffdf7",
      backgroundMode: "inherit",
      flowRegions: [
        {
          id: "newsletter_left",
          name: "Left column",
          x: 24,
          y: 72,
          width: 174,
          height: 468,
          gap: 8,
        },
        {
          id: "newsletter_right",
          name: "Right column",
          x: 222,
          y: 72,
          width: 174,
          height: 468,
          gap: 8,
          nextRegionId: null,
        },
      ],
      nodes: [
        n("newsletter_heading", "text", 24, 35, 372, 32, {
          styleId: "newsletter_title_style",
          typographyMode: "inherit",
          fieldId: "newsletterTitle",
          fontFamily: "Georgia",
          fontSize: 21,
        }),
        n("newsletter_body", "richtext", 24, 72, 174, 24, {
          styleId: "newsletter_body_style",
          typographyMode: "inherit",
          fieldId: "newsletterStory",
          fontSize: 11,
          lineHeight: 1.4,
          placement: "flow",
          flowRegionId: "newsletter_left",
          overflow: "continue",
        }),
      ],
    }),
  ],
  settings: {
    header: "STUDIO JOURNAL",
    footer: "{page} / {pages}",
    bandHeight: 18,
    headerStyleId: "text_role_caption",
    footerStyleId: "text_role_caption",
  },
  outputs: outputs.map((o) => ({ ...o, id: `newsletter_${o.id}` })),
});
export const directoryCard: Project = parseProject({
  ...businessCard,
  id: "directory-card",
  name: "Team directory card",
  description:
    "Select a person from a versioned table to generate a two-sided card without a database.",
  datasets: [
    {
      id: "team_directory",
      name: "Team directory",
      version: 1,
      kind: "table",
      primaryKey: "id",
      labelColumn: "name",
      columns: [
        { key: "id", label: "ID" },
        { key: "name", label: "Name" },
        { key: "titleField", label: "Job title" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "websiteCard", label: "Website" },
      ],
      rows: [
        {
          id: "alex",
          name: "Alex Morgan",
          titleField: "Creative director",
          email: "alex@example.com",
          phone: "+1 (512) 555-0142",
          websiteCard: "example.com",
        },
        {
          id: "jamie",
          name: "Jamie Rivera",
          titleField: "Design lead",
          email: "jamie@example.com",
          phone: "+1 (512) 555-0175",
          websiteCard: "studio.example.com",
        },
      ],
    },
  ],
  fields: [
    f("directoryPerson", "Team member", "dropdown", {
      default: "alex",
      order: -1,
      required: true,
      source: {
        kind: "dataset",
        datasetId: "team_directory",
        datasetVersion: 1,
      },
    }),
    ...businessCard.fields.map((field) =>
      field.id === "company"
        ? field
        : {
            ...field,
            source: {
              kind: "lookup",
              datasetId: "team_directory",
              datasetVersion: 1,
              lookupFieldId: "directoryPerson",
              resultColumn: field.id,
            },
          },
    ),
  ],
});
export const profileCard: Project = parseProject({
  ...businessCard,
  id: "profile-card",
  name: "Profile-driven business card",
  description: "User and order lookups supplied by a replaceable host adapter.",
  instructions:
    "Switch the demo profile to resolve user data. The partner part overrides only the title source and company value.",
  fields: [
    ...businessCard.fields.map((field) => ({
      ...field,
      editable: false,
      default:
        field.id === "name"
          ? "Guest visitor"
          : field.id === "titleField"
            ? "Your title"
            : "",
      source:
        field.id === "company"
          ? { kind: "dynamic", key: "companyName" }
          : {
              kind: "user",
              key: (
                {
                  name: "lookup_1",
                  titleField: "lookup_5",
                  email: "lookup_14",
                  phone: "lookup_2",
                  websiteCard: "lookup_19",
                } as Record<string, string>
              )[field.id],
            },
    })),
    f("orderRef", "Order number", "text", {
      editable: false,
      source: { kind: "order", key: "ORDER_DISP_NO" },
      default: "~{ORDER_DISP_NO}~",
      order: 6,
    }),
  ],
  parts: [
    {
      id: "partner",
      name: "Partner card",
      sku: "PARTNER-CARD",
      overrides: { titleField: { source: { kind: "user", key: "lookup_20" } } },
      systemValues: { company: "Partner Studio" },
    },
  ],
  pages: businessCard.pages.map((page, i) =>
    i === 1
      ? {
          ...page,
          nodes: [
            ...page.nodes,
            n("card_order", "text", 20, 104, 212, 12, {
              fieldId: "orderRef",
              fontSize: 8,
            }),
          ],
        }
      : page,
  ),
});
export const officeCard: Project = parseProject({
  ...businessCard,
  id: "office-card",
  name: "Office profile card",
  description:
    "Select a prepopulation record to fill contact variables, then edit them individually.",
  instructions:
    "Choose an office profile. The satellite part supplies its own records while other field settings inherit.",
  fields: [
    f("office", "Office profile", "preset", {
      order: -1,
      default: "north",
      presets: [
        {
          id: "north",
          label: "North studio",
          values: {
            name: "Alex Morgan",
            titleField: "North studio director",
            email: "alex@north.example.com",
            phone: "+1 (512) 555-0142",
            company: "NORTH STUDIO",
          },
        },
        {
          id: "south",
          label: "South studio",
          values: {
            name: "Jamie Rivera",
            titleField: "South studio lead",
            email: "jamie@south.example.com",
            phone: "+1 (512) 555-0175",
            company: "SOUTH STUDIO",
          },
        },
      ],
    }),
    ...businessCard.fields,
  ],
  parts: [
    {
      id: "satellite",
      sku: "SATELLITE",
      name: "Satellite offices",
      overrides: {
        office: {
          presets: [
            {
              id: "north",
              label: "North satellite",
              values: {
                name: "Alex Morgan",
                titleField: "North satellite director",
                email: "alex@satellite.example.com",
                company: "SATELLITE STUDIO",
              },
            },
            {
              id: "south",
              label: "South satellite",
              values: {
                name: "Jamie Rivera",
                titleField: "South satellite lead",
                email: "jamie@satellite.example.com",
                company: "SATELLITE STUDIO",
              },
            },
          ],
        },
      },
      systemValues: {},
    },
  ],
});
export const locationCard: Project = parseProject({
  schemaVersion: 1,
  id: "location-card",
  name: "Location invitation",
  description:
    "Find a demo location and generate an image for the live proof and export.",
  instructions:
    "Search Austin, Chicago or Seattle. Demo maps are schematic illustrations; hosts can supply a geographic map provider.",
  fields: [
    f("location_title", "Invitation title", "text", {
      default: "Meet us at the studio",
    }),
    f("location_map", "Location map", "map", {
      required: true,
      image: { tools: [] },
      map: { width: 640, height: 360, zoom: 14 },
    }),
  ],
  parts: [
    {
      id: "large-map",
      name: "High-resolution map",
      sku: "MAP-LARGE",
      overrides: {
        location_map: {
          map: { width: 1280, height: 720, zoom: 15, style: "roadmap" },
        },
      },
      systemValues: {},
    },
  ],
  pages: [
    pageSchema.parse({
      id: "location-page",
      name: "Invitation",
      width: 420,
      height: 300,
      background: "transparent",
      nodes: [
        n("location_heading", "text", 24, 22, 372, 36, {
          fieldId: "location_title",
          fontSize: 24,
        }),
        n("location_picture", "map", 24, 74, 372, 200, {
          fieldId: "location_map",
        }),
      ],
    }),
  ],
  outputs: outputs.map((output) => ({
    ...output,
    id: `location_${output.id}`,
  })),
});
export const brandCard = parseProject({
  schemaVersion: 1,
  id: "brand-card",
  tenantId: "demo",
  name: "Brand library card",
  description:
    "Browse an approved image library, select a retained image and try a part with another root folder.",
  fields: [
    f("brand_name", "Company name", "text", {
      default: "Northstar Studio",
      required: true,
    }),
    f("brand_image", "Brand image", "image-list", {
      source: { kind: "dam", providerId: "demo-dam", folderId: "brand" },
      required: true,
      image: {
        allowUpload: false,
        tools: ["crop", "rotate", "grayscale", "opacity"],
      },
    }),
  ],
  parts: [
    {
      id: "portrait-part",
      sku: "BRAND-PORTRAIT",
      name: "Portrait artwork",
      overrides: {
        brand_image: {
          source: {
            kind: "dam",
            providerId: "demo-dam",
            folderId: "portraits",
          },
        },
      },
    },
  ],
  pages: [
    pageSchema.parse({
      id: "brand-page",
      name: "Brand card",
      width: 360,
      height: 240,
      background: null,
      nodes: [
        n("brand_title_node", "text", 24, 24, 312, 36, {
          fieldId: "brand_name",
          fontSize: 24,
        }),
        n("brand_image_node", "image", 90, 78, 160, 140, {
          fieldId: "brand_image",
        }),
      ],
    }),
  ],
  outputs: outputs.map((output) => ({ ...output, id: `brand_${output.id}` })),
});
export const statementLetter = parseProject({
  schemaVersion: 1,
  id: "statement-letter",
  tenantId: "demo",
  name: "Reusable statement letter",
  description:
    "Choose versioned reusable text, edit custom content, and compare a part-specific default.",
  datasets: [
    {
      ...newStatementDataset("letter_messages", "Letter messages"),
      rows: [
        {
          id: "welcome",
          name: "Welcome",
          content:
            "Thank you for choosing our team.\nWe look forward to working with you.",
        },
        {
          id: "formal",
          name: "Formal introduction",
          content:
            "We appreciate the opportunity to support your business.\nPlease contact our team with any questions.",
        },
      ],
    },
    {
      ...newStatementDataset("letter_closings", "Letter closings"),
      rows: [
        {
          id: "thanks",
          name: "Warm thanks",
          content: "With warm thanks,\nThe Northstar team",
        },
        {
          id: "regards",
          name: "Kind regards",
          content: "Kind regards,\nYour account team",
        },
      ],
    },
  ],
  fields: [
    f("recipient", "Recipient", "text", {
      default: "Jamie Rivera",
      required: true,
    }),
    f("message", "Message", "textarea", {
      required: true,
      source: {
        kind: "statement",
        datasetId: "letter_messages",
        datasetVersion: 1,
        resultColumn: "content",
        labelColumn: "name",
        statementId: "welcome",
      },
    }),
    f("closing", "Closing", "textarea", {
      source: {
        kind: "statement",
        datasetId: "letter_closings",
        datasetVersion: 1,
        resultColumn: "content",
        labelColumn: "name",
        statementId: "thanks",
      },
    }),
  ],
  parts: [
    {
      id: "formal-letter",
      sku: "LETTER-FORMAL",
      name: "Formal letter",
      overrides: {
        message: {
          source: {
            kind: "statement",
            datasetId: "letter_messages",
            datasetVersion: 1,
            resultColumn: "content",
            labelColumn: "name",
            statementId: "formal",
          },
        },
      },
    },
  ],
  pages: [
    pageSchema.parse({
      id: "letter",
      name: "Letter",
      width: 360,
      height: 480,
      nodes: [
        n("letter_recipient", "text", 32, 32, 296, 40, {
          fieldId: "recipient",
          fontSize: 22,
        }),
        n("letter_message", "text", 32, 112, 296, 180, {
          fieldId: "message",
          fontSize: 14,
        }),
        n("letter_closing", "text", 32, 330, 296, 80, {
          fieldId: "closing",
          fontSize: 14,
        }),
      ],
    }),
  ],
  outputs: outputs.map((output) => ({
    ...output,
    id: `statement_${output.id}`,
  })),
});
export const dynamicCatalogCard: Project = parseProject({
  ...structuredClone(freeform),
  id: "dynamic-catalog-card",
  name: "Dynamic catalogue card",
  description:
    "Host-owned choice catalogs feed generated controls, live proof and native exports.",
  instructions:
    "Switch Alex, Jamie and Guest to load different host-owned options. Select a service and format, then generate a proof.",
  fields: [
    f("catalogService", "Service", "dropdown", {
      required: true,
      source: {
        kind: "dynamic",
        key: "directoryServices",
        dynamicMode: "choices",
      },
    }),
    f("catalogFormat", "Delivery format", "radio", {
      source: {
        kind: "dynamic",
        key: "directoryFormats",
        dynamicMode: "choices",
      },
    }),
    f("catalogTeam", "Directory team", "text", {
      editable: false,
      system: true,
      source: { kind: "dynamic", key: "directoryTeam" },
    }),
  ],
  groups: [],
  tabs: [],
  parts: [
    {
      id: "partner",
      name: "Partner",
      sku: "CATALOG-PARTNER",
      overrides: { catalogService: { editable: false } },
      systemValues: { catalogService: "consulting" },
    },
  ],
  forms: [],
  formInstances: [],
  triggers: [],
  datasets: [],
  resourceSnapshots: [],
  pages: [
    pageSchema.parse({
      id: "catalog_card_page",
      name: "Directory card",
      width: null,
      height: null,
      background: null,
      nodes: [
        n("catalog_title", "text", 0, 0, 330, 36, {
          content: "Directory services",
          fontSize: 25,
        }),
        n("catalog_team", "text", 0, 55, 330, 30, {
          fieldId: "catalogTeam",
          fontSize: 16,
        }),
        n("catalog_service", "text", 0, 100, 330, 36, {
          fieldId: "catalogService",
          fontSize: 22,
        }),
        n("catalog_format", "text", 0, 150, 330, 30, {
          fieldId: "catalogFormat",
          fontSize: 16,
        }),
      ],
    }),
  ],
});
export const regionalCatalogCard = parseProject({
  ...structuredClone(dynamicCatalogCard),
  id: "regional-catalog-card",
  name: "Regional catalogue card",
  instructions:
    "Open Source parameters to change the region, load current services and review the proof. Refresh source data to bypass a host cache.",
  dynamicSources: [
    {
      id: "regional-directory",
      name: "Regional directory",
      parameters: [
        {
          key: "region",
          label: "Region",
          type: "dropdown",
          required: true,
          default: "North",
          choices: [
            { value: "North", label: "North" },
            { value: "South", label: "South" },
          ],
        },
      ],
    },
  ],
  fields: dynamicCatalogCard.fields.map((field) => ({
    ...field,
    source: { ...field.source, dynamicSourceId: "regional-directory" },
  })),
});
export const optionalDirectoryCard = parseProject({
  ...structuredClone(regionalCatalogCard),
  id: "optional-directory-card",
  name: "Optional directory card",
  instructions:
    "Load the directory or explicitly skip it to use configured values. The Partner part requires its directory source.",
  dynamicSources: regionalCatalogCard.dynamicSources!.map((source) => ({
    ...source,
    allowSkip: true,
  })),
  fields: regionalCatalogCard.fields.map((field) => ({
    ...field,
    default:
      field.id === "catalogTeam"
        ? "Configured directory"
        : field.id === "catalogFormat"
          ? "digital"
          : "consulting",
    choices:
      field.id === "catalogService"
        ? [{ value: "consulting", label: "Consulting" }]
        : field.id === "catalogFormat"
          ? [{ value: "digital", label: "Digital" }]
          : field.choices,
    source: { ...field.source, dynamicSourceId: "regional-directory" },
  })),
  parts: dynamicCatalogCard.parts.map((part) => ({
    ...part,
    dynamicSourcePolicies: { "regional-directory": "required" },
  })),
});
export const demoProjects = () =>
  structuredClone([
    botanical,
    businessCard,
    freeform,
    newsletter,
    directoryCard,
    profileCard,
    officeCard,
    locationCard,
    brandCard,
    statementLetter,
    dynamicCatalogCard,
    regionalCatalogCard,
    optionalDirectoryCard,
  ]);
